import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  reasonsToDeclineAllQuotes,
  reasonToDeclineReferredQuote,
  lossTypes,
  capacityTypes,
} from '@components/modals/DeclinationModal/DeclinationModal.constants';
import { Dialog } from '@components/dialog';
import { QuoteStatus, QuoteStatusId } from '@models/quotes';
import { NoteType } from '@models/note';
import { ToastType } from '@components/toast/ToastType.enum';
import * as styles from '@components/modals/Modal.styles';
import { Form, FormSection } from '@components/forms';
import { getFormFields } from '@components/modals/DeclinationModal/getFormFields';
import { useCreateQuoteStatusChange } from '@services/quotes';
import { useInsertNote } from '@services/note';
import { DeclinationModalFormValues } from '@components/modals/DeclinationModal/DeclinatonModalFormValues.type';
import { formatFormValues } from '@components/forms/formatFormValues';

type DeclinationModalProps = {
  isOpen: boolean;
  quoteId: string;
  policyNumber: string;
  currentStatusId: string;
  customerId: string;
  brokerageId: string;
  onCloseModal: () => void;
  hasInternalOnlyAccessRestriction: boolean;
  onSuccess?: () => void;
};

export const DeclinationModal: React.FC<DeclinationModalProps> = ({
  isOpen,
  quoteId,
  policyNumber,
  currentStatusId,
  customerId,
  brokerageId,
  onCloseModal,
  hasInternalOnlyAccessRestriction,
  onSuccess,
}) => {
  const { createQuoteStatusChange, isPending: isLoading } = useCreateQuoteStatusChange();
  const { insertNoteAsync } = useInsertNote();

  const methods = useForm<DeclinationModalFormValues>({
    defaultValues: { shouldSkipEmail: 'false' },
    mode: 'onChange',
  });

  const { enqueueSnackbar } = useSnackbar();

  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const clearForm = () => {
    methods.reset({ reason: undefined });
  };

  const selectedReason: string | undefined = methods.watch('reason');

  const declinationReasons: string[] =
    currentStatusId !== QuoteStatus.Referred.id
      ? reasonsToDeclineAllQuotes
      : reasonToDeclineReferredQuote.concat(reasonsToDeclineAllQuotes);

  const isValid = methods.formState.isValid;

  const typeFieldItems: string[] = React.useMemo(() => {
    return selectedReason === 'Loss History' ? lossTypes : selectedReason === 'Capacity' ? capacityTypes : [];
  }, [selectedReason]);

  const fields = useMemo(() => {
    return getFormFields({ declinationReasons, typeFieldItems, hasInternalOnlyAccessRestriction });
  }, [declinationReasons, hasInternalOnlyAccessRestriction, typeFieldItems]);

  useEffect(() => {
    methods.reset({
      ...methods.getValues(),
      reason: selectedReason,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReason]);

  const handleDeclineQuote = async () => {
    if (!quoteId) {
      return;
    }

    const values = methods.getValues();
    const formData = formatFormValues(values, fields, { visibleOnly: true });

    const messageToBroker = formData.messageToBroker;
    const declineQuoteBody = {
      quoteId,
      statusId: QuoteStatusId.Declined,
      quoteStatusChangeReason: {
        reason: formData.reason,
        description: formData.notes,
        details: {
          carrier: formData.carrier,
          otherCarrier: formData.otherCarrier,
          premium: formData.premium,
          message: messageToBroker,
        },
      },
      shouldSkipEmail: formData.shouldSkipEmail,
    };

    createQuoteStatusChange(declineQuoteBody, {
      onError: () => {
        enqueueSnackbar(`Declination of quote #${policyNumber} has failed.`, {
          variant: ToastType.Error,
          preventDuplicate: true,
        });
      },
      onSuccess: () => {
        onSuccess?.();
        if (messageToBroker) {
          const insertNoteBody = {
            type: NoteType.Message,
            body: messageToBroker,
            rawText: messageToBroker,
            quoteId,
            customerId,
            brokerageId,
          };
          insertNoteAsync({ noteRequestBody: insertNoteBody });
        }
        clearForm();
        onCloseModal();
        enqueueSnackbar(`Quote #${policyNumber} was successfully declined.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      },
    });
  };

  return (
    <Dialog
      handleCloseDialog={handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading}
      open={isOpen}
      primaryBtnAction={handleDeclineQuote}
      primaryBtnText='Decline Quote'
      secondaryBtnAction={handleExit}
      secondaryBtnText='Exit'
      title={`Declination of Quote #${policyNumber}`}
    >
      <Form methods={methods} formId='declination-modal-form'>
        <Box sx={{ ...styles.modalContent, padding: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
